/**
 * Absence Service
 * 
 * API calls for absence domain
 */

import { apiClient } from '@/api/core/apiClient';
import { API_CONFIG } from '@/api/core/apiConfig';
import type { ApiResponse } from '@/api/shared/types';
import { buildRequiredQueryString, buildQueryString } from '@/api/shared/queryBuilder';
import type {
  LC_ListAbsencesIn,
  LC_ListAbsencesOut,
  LC_ExportAbsencesIn,
  LC_ListAbsencesByScopeIn,
  LC_ListAbsencesByScopeListOut,
  AbsenceListResult,
  AbsenceScopeListResult,
  LC_GetEmployeesForAbsenceIn,
  LC_GetEmployeesForAbsenceOut,
  LC_GetMedicalLeaveTypesOut,
  LC_CreateMedicalLeaveIn,
  LC_CreateMedicalLeaveOut,
  LC_CalculateReturnDateOut,
  LC_GetAbsenceWorkdayTypesOut,
  LC_GetSpecialLeaveTypesOut,
  LC_CreateSpecialLeaveIn,
  LC_CreateSpecialLeaveOut,
} from './absence.types';
import { mapAbsenceListResponse, mapAbsenceScopeListResponse } from './absence.mapper';


export class AbsenceService {
  /**
   * Get paginated list of absences with totals
   */
  async list(input: LC_ListAbsencesIn): Promise<ApiResponse<AbsenceListResult>> {
    const queryString = buildRequiredQueryString(
      { ...input },
      ['startDate', 'endDate', 'absenceType', 'status'],
    );
    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE.LIST}${queryString}`;

    const response = await apiClient.get<LC_ListAbsencesOut>(endpoint);

    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }

    return {
      success: true,
      data: mapAbsenceListResponse(response.data),
      status: response.status,
    };
  }

  /**
   * Get paginated list of absences for management scope view (multi-employee)
   */
  async listByScope(input: LC_ListAbsencesByScopeIn): Promise<ApiResponse<AbsenceScopeListResult>> {
    const queryString = buildRequiredQueryString(
      { ...input },
      ['startDate', 'endDate', 'absenceType', 'status'],
    );
    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE.SCOPE_LIST}${queryString}`;

    const response = await apiClient.get<LC_ListAbsencesByScopeListOut>(endpoint);

    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }

    return {
      success: true,
      data: mapAbsenceScopeListResponse(response.data),
      status: response.status,
    };
  }

  /**
   * Export absences to Excel file
   */
  async exportAbsences(input: LC_ExportAbsencesIn)
    : Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', input.startDate);
    searchParams.append('endDate', input.endDate);
    searchParams.append('absenceType', input.absenceType);
    searchParams.append('status', input.status);
    searchParams.append('format', input.format);

    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE.FILE}?${searchParams.toString()}`;
    return apiClient.downloadBlobWithFilename(endpoint, { method: 'GET' });
  }

  /**
   * Get paginated list of employees available for absence creation
   */
  async getEmployeesForAbsence(
    input: LC_GetEmployeesForAbsenceIn,
  ): Promise<ApiResponse<LC_GetEmployeesForAbsenceOut>> {
    const queryString = buildQueryString({ ...input });
    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE.EMPLOYEES}${queryString}`;
    return apiClient.get<LC_GetEmployeesForAbsenceOut>(endpoint);
  }

  /**
   * Get available medical leave types
   */
  async getMedicalLeaveTypes(): Promise<ApiResponse<LC_GetMedicalLeaveTypesOut>> {
    return apiClient.get<LC_GetMedicalLeaveTypesOut>(
      API_CONFIG.ENDPOINTS.MEDICAL_LEAVE.TYPES,
    );
  }

  /**
   * Create a new medical leave record
   */
  async createMedicalLeave(
    input: LC_CreateMedicalLeaveIn,
  ): Promise<ApiResponse<LC_CreateMedicalLeaveOut>> {
    return apiClient.post<LC_CreateMedicalLeaveOut>(
      API_CONFIG.ENDPOINTS.MEDICAL_LEAVE.CREATE,
      input,
    );
  }

  /**
   * Calculate the system return date (next business day) for a given employee/end date.
   * Shared across all absence types (medical leave, special leave, etc.).
   */
  async calculateReturnDate(
    params: { employeeTargetId: number; endDate: string; workdayType?: string },
  ): Promise<ApiResponse<LC_CalculateReturnDateOut>> {
    const queryString = buildQueryString({ ...params });
    return apiClient.get<LC_CalculateReturnDateOut>(
      `${API_CONFIG.ENDPOINTS.ABSENCE.RETURN_DATE}${queryString}`,
    );
  }

  /**
   * Get available workday types shared by absence creation flows
   */
  async getAbsenceWorkdayTypes(): Promise<ApiResponse<LC_GetAbsenceWorkdayTypesOut>> {
    return apiClient.get<LC_GetAbsenceWorkdayTypesOut>(
      API_CONFIG.ENDPOINTS.ABSENCE.WORKDAY_TYPES,
    );
  }

  /**
   * Get available special leave types (company-specific catalog)
   */
  async getSpecialLeaveTypes(): Promise<ApiResponse<LC_GetSpecialLeaveTypesOut>> {
    return apiClient.get<LC_GetSpecialLeaveTypesOut>(
      API_CONFIG.ENDPOINTS.SPECIAL_LEAVE.TYPES,
    );
  }

  /**
   * Create a new special leave record
   */
  async createSpecialLeave(
    input: LC_CreateSpecialLeaveIn,
  ): Promise<ApiResponse<LC_CreateSpecialLeaveOut>> {
    return apiClient.post<LC_CreateSpecialLeaveOut>(
      API_CONFIG.ENDPOINTS.SPECIAL_LEAVE.CREATE,
      input,
    );
  }
}

export const absenceService = new AbsenceService();
